import { useState, useEffect } from "react";

export default function VegasPage() {
  const [form, setForm] = useState({ name: "", school: "", email: "", tier: "", source: "" });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const tiers = [
    { value: "starter", label: "Starter (1–200 athletes)" },
    { value: "varsity", label: "Varsity (201–400 athletes)" },
    { value: "all-american", label: "All-American (400+ athletes)" },
    { value: "unsure", label: "Not sure yet" },
  ];

  const sources = [
    { value: "keynote", label: "PDI Keynote — Saturday morning session" },
    { value: "workshop", label: "Leader/Manager Workshop — Christine's session" },
    { value: "coffeebar", label: "Coffee bar / around the conference" },
  ];

  const isValid = () =>
    form.name.trim().length > 1 &&
    form.school.trim().length > 1 &&
    /^[^\s@]+@[^\s@]+\.[a-zA-Z]{2,}$/.test(form.email) &&
    form.tier !== "" &&
    form.source !== "";

  async function handleSubmit() {
    if (!isValid()) return;
    setLoading(true);
    setError("");
    try {
      const res = await fetch("/.netlify/functions/vegasLead", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      if (!res.ok) throw new Error("Submission failed");
      setSubmitted(true);
    } catch {
      setError("Something went wrong. Try again or find us at the booth.");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    if (!submitted) return;
    if (document.querySelector('script[src*="calendly"]')) return;
    const link = document.createElement("link");
    link.href = "https://assets.calendly.com/assets/external/widget.css";
    link.rel = "stylesheet";
    document.head.appendChild(link);
    const script = document.createElement("script");
    script.src = "https://assets.calendly.com/assets/external/widget.js";
    script.async = true;
    document.body.appendChild(script);
  }, [submitted]);

  if (submitted) {
    return (
      <div style={styles.page}>
        <div style={styles.card}>
          <div style={styles.checkmark}>✓</div>
          <h1 style={styles.thankYouTitle}>We've got you.</h1>
          <p style={styles.thankYouSub}>
            Full overview is on its way. Lock in a demo below while you're thinking about it.
          </p>
          <div style={styles.divider} />
          <p style={styles.thankYouNote}>
            We're at PDI and NACDA Convention all week (June 5–11, Mandalay Bay).
            Come find us at the coffee bar Monday and Tuesday mornings, or just look for the two tallest people in the room.
          </p>
          <div style={styles.divider} />
          <div
            className="calendly-inline-widget"
            data-url="https://calendly.com/overtimesolutions/corporate-combine-30-min-demo?hide_event_type_details=1&hide_gdpr_banner=1&primary_color=3a2000"
            style={{ minWidth: "100%", height: 660 }}
          />
          <div style={styles.footer}>
            <span style={styles.footerText}>The Corporate Combine™ · corporatecombine.com</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.page}>
      <div style={styles.card}>

        {/* Header */}
        <div style={styles.header}>
          <div style={styles.badge}>N4A Trusted Partner · NACDA 2026</div>
          <h1 style={styles.brand}>The Corporate Combine™</h1>
          <div style={styles.brandSub}>Overtime Solutions LLC</div>
        </div>

        <div style={styles.divider} />

        {/* Headline */}
        <div style={styles.heroSection}>
          <h2 style={styles.headline}>When the game is over,<br />who are they?</h2>
          <p style={styles.subheadline}>Every athlete's career ends. Only some are ready.</p>
        </div>

        {/* Stats */}
        <div style={styles.statsRow}>
          <div style={styles.stat}>
            <div style={styles.statNum}>#1</div>
            <div style={styles.statLabel}>Resource request among D1 athletes — above mental health, NIL &amp; nutrition</div>
          </div>
          <div style={styles.statDivider} />
          <div style={styles.stat}>
            <div style={styles.statNum}>38%</div>
            <div style={styles.statLabel}>Of women athletes said planning for the future harmed their mental health</div>
          </div>
          <div style={styles.statDivider} />
          <div style={styles.stat}>
            <div style={styles.statNum}>6,789</div>
            <div style={styles.statLabel}>D1 athletes surveyed. Career services existed at every one of those schools.</div>
          </div>
        </div>
        <div style={styles.statsSource}>Source: NCAA SNAP Study, October 2025</div>

        <div style={styles.divider} />

        {/* Hook */}
        <p style={styles.hook}>
          Your athletes are thinking about this. They're just not saying it out loud.
          We give them the language — and a 20-minute path to figuring out who they are after sport.
          Enter your info below and we'll send you everything you need to see the full picture.
        </p>

        {/* Form */}
        <div style={styles.form}>
          <div style={styles.fieldGroup}>
            <label style={styles.label}>Full Name</label>
            <input
              style={styles.input}
              placeholder="Your name"
              value={form.name}
              onChange={e => setForm({ ...form, name: e.target.value })}
            />
          </div>

          <div style={styles.fieldGroup}>
            <label style={styles.label}>Institution</label>
            <input
              style={styles.input}
              placeholder="School or organization"
              value={form.school}
              onChange={e => setForm({ ...form, school: e.target.value })}
            />
          </div>

          <div style={styles.fieldGroup}>
            <label style={styles.label}>Email Address</label>
            <input
              style={styles.input}
              type="email"
              placeholder="you@school.edu"
              value={form.email}
              onChange={e => setForm({ ...form, email: e.target.value })}
            />
          </div>

          <div style={styles.fieldGroup}>
            <label style={styles.label}>Program Size</label>
            <div style={styles.tierGrid}>
              {tiers.map(t => (
                <div
                  key={t.value}
                  style={{
                    ...styles.tierOption,
                    ...(form.tier === t.value ? styles.tierSelected : {})
                  }}
                  onClick={() => setForm({ ...form, tier: t.value })}
                >
                  {t.label}
                </div>
              ))}
            </div>
          </div>

          <div style={styles.fieldGroup}>
            <label style={styles.label}>How did we connect?</label>
            <div style={styles.sourceGrid}>
              {sources.map(s => (
                <div
                  key={s.value}
                  style={{
                    ...styles.tierOption,
                    ...(form.source === s.value ? styles.tierSelected : {})
                  }}
                  onClick={() => setForm({ ...form, source: s.value })}
                >
                  {s.label}
                </div>
              ))}
            </div>
          </div>

          {error && <p style={styles.errorText}>{error}</p>}

          <button
            style={{
              ...styles.submitBtn,
              ...(loading || !isValid() ? styles.submitDisabled : {})
            }}
            onClick={handleSubmit}
            disabled={loading || !isValid()}
          >
            {loading ? "Sending..." : "Send Me the Overview →"}
          </button>

          <p style={styles.disclaimer}>
            No spam. Just the overview and a follow-up if you want one.
          </p>
        </div>

        <div style={styles.footer}>
          <span style={styles.footerText}>The Corporate Combine™ · Overtime Solutions LLC · corporatecombine.com</span>
        </div>
      </div>
    </div>
  );
}

const T = {
  goldDark: "#3a2000",
  goldMid: "#7a4f00",
  goldLight: "#c8960a",
  goldPale: "#e8c44a",
  cream: "#f7ecd0",
  glass: "rgba(255,255,255,0.28)",
  glassBorder: "rgba(255,255,255,0.52)",
};

const styles = {
  page: {
    minHeight: "100vh",
    background: "linear-gradient(160deg, #f5e6c0 0%, #e8c44a 55%, #c8960a 100%)",
    display: "flex",
    justifyContent: "center",
    alignItems: "flex-start",
    padding: "40px 16px",
    fontFamily: "'DM Sans', system-ui, sans-serif",
  },
  card: {
    width: "100%",
    maxWidth: 560,
    background: T.glass,
    border: `1px solid ${T.glassBorder}`,
    borderRadius: 20,
    padding: "36px 32px",
    backdropFilter: "blur(12px)",
  },
  header: {
    textAlign: "center",
    marginBottom: 20,
  },
  badge: {
    display: "inline-block",
    background: T.goldDark,
    color: T.cream,
    fontSize: 10,
    fontWeight: 600,
    letterSpacing: "0.14em",
    textTransform: "uppercase",
    padding: "4px 14px",
    borderRadius: 20,
    marginBottom: 12,
  },
  brand: {
    fontFamily: "Georgia, serif",
    fontSize: 28,
    fontWeight: 900,
    color: T.goldDark,
    margin: "0 0 4px",
    lineHeight: 1.1,
  },
  brandSub: {
    fontSize: 11,
    color: T.goldMid,
    letterSpacing: "0.12em",
    textTransform: "uppercase",
  },
  divider: {
    height: 1,
    background: "rgba(58,32,0,0.15)",
    margin: "20px 0",
  },
  heroSection: {
    textAlign: "center",
    marginBottom: 24,
  },
  headline: {
    fontFamily: "Georgia, serif",
    fontSize: 32,
    fontWeight: 900,
    color: T.goldDark,
    lineHeight: 1.15,
    margin: "0 0 10px",
  },
  subheadline: {
    fontFamily: "Georgia, serif",
    fontSize: 16,
    fontStyle: "italic",
    color: T.goldMid,
    margin: 0,
  },
  statsRow: {
    display: "flex",
    background: T.goldDark,
    borderRadius: 12,
    padding: "16px 0",
    marginBottom: 6,
  },
  stat: {
    flex: 1,
    textAlign: "center",
    padding: "0 12px",
  },
  statNum: {
    fontFamily: "Georgia, serif",
    fontSize: 26,
    fontWeight: 900,
    color: T.goldPale,
    lineHeight: 1,
    marginBottom: 4,
  },
  statLabel: {
    fontSize: 10,
    color: "rgba(247,236,208,0.7)",
    letterSpacing: "0.04em",
    lineHeight: 1.4,
  },
  statDivider: {
    width: 1,
    background: "rgba(255,255,255,0.15)",
    flexShrink: 0,
  },
  statsSource: {
    fontSize: 10,
    color: T.goldMid,
    textAlign: "center",
    letterSpacing: "0.06em",
    marginBottom: 4,
  },
  hook: {
    fontSize: 14,
    color: T.goldDark,
    lineHeight: 1.7,
    textAlign: "center",
    marginBottom: 4,
  },
  form: {
    display: "flex",
    flexDirection: "column",
    gap: 16,
  },
  fieldGroup: {
    display: "flex",
    flexDirection: "column",
    gap: 6,
  },
  label: {
    fontSize: 11,
    fontWeight: 600,
    letterSpacing: "0.12em",
    textTransform: "uppercase",
    color: T.goldMid,
  },
  input: {
    background: "rgba(255,255,255,0.5)",
    border: "1px solid rgba(58,32,0,0.2)",
    borderRadius: 10,
    padding: "12px 14px",
    fontSize: 14,
    color: T.goldDark,
    outline: "none",
    fontFamily: "inherit",
  },
  tierGrid: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: 8,
  },
  sourceGrid: {
    display: "grid",
    gridTemplateColumns: "1fr",
    gap: 8,
  },
  tierOption: {
    background: "rgba(255,255,255,0.4)",
    border: "1px solid rgba(58,32,0,0.15)",
    borderRadius: 8,
    padding: "10px 12px",
    fontSize: 12,
    color: T.goldMid,
    cursor: "pointer",
    textAlign: "center",
    transition: "all 0.15s",
  },
  tierSelected: {
    background: T.goldDark,
    border: `1px solid ${T.goldDark}`,
    color: T.cream,
    fontWeight: 600,
  },
  errorText: {
    fontSize: 13,
    color: "#c0392b",
    textAlign: "center",
  },
  submitBtn: {
    background: `linear-gradient(135deg, ${T.goldDark}, ${T.goldMid})`,
    color: T.cream,
    border: "none",
    borderRadius: 12,
    padding: "14px 24px",
    fontSize: 15,
    fontWeight: 600,
    cursor: "pointer",
    letterSpacing: "0.04em",
    fontFamily: "inherit",
  },
  submitDisabled: {
    opacity: 0.5,
    cursor: "not-allowed",
  },
  disclaimer: {
    fontSize: 11,
    color: T.goldMid,
    textAlign: "center",
    fontStyle: "italic",
  },
  footer: {
    textAlign: "center",
    marginTop: 20,
    paddingTop: 16,
    borderTop: "1px solid rgba(58,32,0,0.12)",
  },
  footerText: {
    fontSize: 11,
    color: T.goldMid,
    letterSpacing: "0.04em",
  },
  checkmark: {
    width: 56,
    height: 56,
    background: T.goldDark,
    borderRadius: "50%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 24,
    color: T.cream,
    margin: "0 auto 20px",
    fontWeight: 700,
  },
  thankYouTitle: {
    fontFamily: "Georgia, serif",
    fontSize: 28,
    fontWeight: 900,
    color: T.goldDark,
    textAlign: "center",
    margin: "0 0 12px",
  },
  thankYouSub: {
    fontSize: 15,
    color: T.goldMid,
    textAlign: "center",
    lineHeight: 1.65,
    margin: "0 0 8px",
  },
  thankYouNote: {
    fontSize: 13,
    color: T.goldMid,
    textAlign: "center",
    lineHeight: 1.65,
    fontStyle: "italic",
  },
  bookBtn: {
    display: "block",
    textAlign: "center",
    background: `linear-gradient(135deg, ${T.goldDark}, ${T.goldMid})`,
    color: T.cream,
    fontSize: 14,
    fontWeight: 600,
    letterSpacing: "0.04em",
    padding: "14px 24px",
    borderRadius: 12,
    textDecoration: "none",
    marginBottom: 4,
  },
};